import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createDepartment, updateDepartment, deleteDepartment, fetchDepartmentMembers, fetchOrgChart, assignDepartmentMember, removeDepartmentMember, fetchAllPages } from '../api';
import { useConfirm } from './ConfirmProvider';

function DepartmentManagement({ authAxios, showFormMessage, userRole, currentUser, users }) {
  const confirm = useConfirm();
  const [departments, setDepartments] = useState([]);
  const [orgChart, setOrgChart] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '', code: '', description: '', head: '', parent: '', is_active: true,
  });

  // Member management
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberUserId, setAddMemberUserId] = useState('');

  // [WF-B4] ACCOUNTANT already has full backend CRUD on Department
  // (permissions.py's unconditional Accountant branch, identical to
  // Partner) — this local gate was the other half of the drift, hiding
  // every manage control even after TAB_ACCESS.departments lets an
  // Accountant see the tab at all.
  const canManage = useMemo(() =>
    ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole) || currentUser?.is_superuser,
    [userRole, currentUser]
  );

  const staffUsers = useMemo(() =>
    (users || []).filter(u => u.is_staff || u.staff_profile),
    [users]
  );

  /* ---- Data Fetching ---- */
  const fetchDepts = useCallback(async () => {
    setIsLoading(true);
    try {
      // fetchAllPages walks every backend page — a plain single GET here
      // (docs/audits/pagination-audit-2026-08-13.md) silently truncated a
      // firm's department list at 10 rows.
      const data = await fetchAllPages(authAxios, 'departments/');
      setDepartments(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load departments.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage]);

  const fetchOrg = useCallback(async () => {
    try {
      const data = await fetchOrgChart(authAxios);
      setOrgChart(Array.isArray(data) ? data : []);
    } catch {}
  }, [authAxios]);

  const loadMembers = useCallback(async (deptId) => {
    try {
      const data = await fetchDepartmentMembers(authAxios, deptId);
      setMembers(Array.isArray(data) ? data : []);
    } catch { setMembers([]); }
  }, [authAxios]);

  useEffect(() => { fetchDepts(); fetchOrg(); }, [fetchDepts, fetchOrg]);
  useEffect(() => { if (selectedDept) loadMembers(selectedDept.id); }, [selectedDept, loadMembers]);

  /* ---- CRUD ---- */
  const resetForm = () => {
    setFormData({ name: '', code: '', description: '', head: '', parent: '', is_active: true });
    setEditingDept(null); setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.head) delete payload.head;
      if (!payload.parent) delete payload.parent;
      if (editingDept) {
        await updateDepartment(authAxios, editingDept.id, payload);
        showFormMessage('success', 'Department updated.');
      } else {
        await createDepartment(authAxios, payload);
        showFormMessage('success', 'Department created.');
      }
      resetForm(); fetchDepts(); fetchOrg();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to save department.';
      showFormMessage('error', detail);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Delete Department', message: 'Delete this department? Members will be unassigned.', confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await deleteDepartment(authAxios, id);
      showFormMessage('success', 'Department deleted.');
      if (selectedDept?.id === id) { setSelectedDept(null); setMembers([]); }
      fetchDepts(); fetchOrg();
    } catch { showFormMessage('error', 'Failed to delete department.'); }
  };

  const handleEdit = (d) => {
    setFormData({
      name: d.name, code: d.code || '', description: d.description || '',
      head: d.head || '', parent: d.parent || '', is_active: d.is_active !== false,
    });
    setEditingDept(d); setShowForm(true);
  };

  /* ---- Member Management ---- */
  const handleAssignMember = async () => {
    if (!addMemberUserId || !selectedDept) return;
    try {
      await assignDepartmentMember(authAxios, selectedDept.id, addMemberUserId);
      showFormMessage('success', 'Member assigned to department.');
      setAddMemberUserId('');
      setShowAddMember(false);
      loadMembers(selectedDept.id);
      fetchDepts(); fetchOrg();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to assign member.');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedDept) return;
    if (!(await confirm({ title: 'Remove Member', message: 'Remove this member from the department?', confirmText: 'Remove', variant: 'danger' }))) return;
    try {
      await removeDepartmentMember(authAxios, selectedDept.id, userId);
      showFormMessage('success', 'Member removed from department.');
      loadMembers(selectedDept.id);
      fetchDepts(); fetchOrg();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to remove member.');
    }
  };

  // Filter users not already in this department for the add-member dropdown
  const availableUsers = useMemo(() => {
    const memberUserIds = new Set(members.map(m => m.user || m.id));
    return staffUsers.filter(u => !memberUserIds.has(u.id));
  }, [staffUsers, members]);

  // Filter departments by search
  const filteredDepts = useMemo(() => {
    if (!searchTerm) return departments;
    const term = searchTerm.toLowerCase();
    return departments.filter(d =>
      d.name.toLowerCase().includes(term) ||
      (d.code || '').toLowerCase().includes(term) ||
      (d.head_name || '').toLowerCase().includes(term)
    );
  }, [departments, searchTerm]);

  /* ---- Recursive Org Chart Renderer ---- */
  const renderOrgNode = (node, depth = 0) => (
    <div key={node.id} className={`${depth > 0 ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''}`}>
      <div className="flex items-center gap-2 py-2 hover:bg-gray-50 rounded px-2 cursor-pointer"
        onClick={() => { setSelectedDept(departments.find(d => d.id === node.id) || node); setActiveSubTab('list'); }}>
        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">
          {(node.code || node.name?.[0] || 'D').toUpperCase().slice(0, 2)}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{node.name}</p>
          <p className="text-xs text-gray-500">
            {node.head_name ? `Head: ${node.head_name}` : 'No head assigned'}
            {node.member_count != null && ` • ${node.member_count} members`}
          </p>
        </div>
      </div>
      {node.children && node.children.length > 0 && (
        <div className="mt-1">
          {node.children.map(child => renderOrgNode(child, depth + 1))}
        </div>
      )}
    </div>
  );

  const subTabs = [
    { id: 'list', label: 'Departments' },
    { id: 'orgChart', label: 'Org Chart' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Departments</h2>
          <p className="text-sm text-gray-500 mt-1">Manage departments and organizational structure</p>
        </div>
        {canManage && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition">
            + New Department
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setActiveSubTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeSubTab === t.id ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search (list tab) */}
      {activeSubTab === 'list' && (
        <div className="mb-4">
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search departments..." className="border rounded-lg px-3 py-2 text-sm w-full max-w-xs" />
        </div>
      )}

      {/* Department Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">{editingDept ? 'Edit Department' : 'New Department'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input type="text" required value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="e.g. Litigation" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input type="text" value={formData.code}
                      onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
                      className="border rounded-lg px-3 py-2 text-sm w-full" placeholder="e.g. LIT" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows={2} value={formData.description}
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                    className="border rounded-lg px-3 py-2 text-sm w-full" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department Head</label>
                    <select value={formData.head} onChange={e => setFormData(p => ({ ...p, head: e.target.value }))}
                      className="border rounded-lg px-3 py-2 text-sm w-full">
                      <option value="">None</option>
                      {staffUsers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Department</label>
                    <select value={formData.parent} onChange={e => setFormData(p => ({ ...p, parent: e.target.value }))}
                      className="border rounded-lg px-3 py-2 text-sm w-full">
                      <option value="">— None (Top-level) —</option>
                      {departments.filter(d => d.id !== editingDept?.id).map(d =>
                        <option key={d.id} value={d.id}>{d.name}</option>
                      )}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="dept-active" checked={formData.is_active}
                    onChange={e => setFormData(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
                  <label htmlFor="dept-active" className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={resetForm}
                    className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit"
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">
                    {editingDept ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isLoading && <div className="text-center py-8 text-gray-500">Loading...</div>}

      {/* ==================== DEPARTMENTS LIST ==================== */}
      {activeSubTab === 'list' && !isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Department Cards */}
          <div className="lg:col-span-2 space-y-3">
            {filteredDepts.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
                {departments.length === 0 ? 'No departments created yet.' : 'No departments match your search.'}
              </div>
            ) : (
              filteredDepts.map(d => (
                <div key={d.id}
                  className={`bg-white rounded-xl shadow-sm border p-5 cursor-pointer transition hover:shadow-md ${selectedDept?.id === d.id ? 'ring-2 ring-blue-400' : ''}`}
                  onClick={() => setSelectedDept(d)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                        {(d.code || d.name[0]).toUpperCase().slice(0, 2)}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800">{d.name}</h4>
                        <p className="text-xs text-gray-500">
                          {d.head_name ? `Head: ${d.head_name}` : 'No head assigned'}
                          {d.parent_name ? ` • Under: ${d.parent_name}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${d.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {d.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-xs text-gray-400">{d.member_count || 0} members</span>
                    </div>
                  </div>
                  {d.description && <p className="text-xs text-gray-500 mt-2">{d.description}</p>}
                  {canManage && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(d); }}
                        className="text-xs text-blue-600 hover:underline">Edit</button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }}
                        className="text-xs text-red-500 hover:underline">Delete</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Right: Selected Department Detail */}
          <div>
            {selectedDept ? (
              <div className="bg-white rounded-xl shadow-sm border p-5 sticky top-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">{selectedDept.name} — Members</h3>
                  {canManage && (
                    <button onClick={() => setShowAddMember(!showAddMember)}
                      className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition">
                      + Add Member
                    </button>
                  )}
                </div>

                {/* Add member dropdown */}
                {showAddMember && canManage && (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Select Staff Member</label>
                    <div className="flex gap-2">
                      <select value={addMemberUserId} onChange={e => setAddMemberUserId(e.target.value)}
                        className="border rounded-lg px-2 py-1.5 text-sm flex-1">
                        <option value="">Choose...</option>
                        {availableUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.username})</option>
                        ))}
                      </select>
                      <button onClick={handleAssignMember} disabled={!addMemberUserId}
                        className="px-3 py-1.5 text-xs text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-40 transition">
                        Assign
                      </button>
                    </div>
                  </div>
                )}

                {members.length === 0 ? (
                  <p className="text-sm text-gray-400">No members in this department.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map(m => {
                      const userId = m.user || m.id;
                      const firstName = m.first_name || m.user_first_name || '';
                      const lastName = m.last_name || m.user_last_name || '';
                      const displayName = `${firstName} ${lastName}`.trim() || m.username || `User #${userId}`;
                      const role = m.role || m.staff_profile?.role || 'Staff';
                      const jobTitle = m.job_title || m.staff_profile?.job_title || '';
                      return (
                        <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 group">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                            {(firstName?.[0] || '') + (lastName?.[0] || '')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{displayName}</p>
                            <p className="text-xs text-gray-500">{role} {jobTitle ? `• ${jobTitle}` : ''}</p>
                          </div>
                          {canManage && (
                            <button onClick={() => handleRemoveMember(userId)}
                              className="text-xs text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                              title="Remove from department">✕</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border p-5 text-center text-gray-400 text-sm">
                Select a department to see members
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== ORG CHART ==================== */}
      {activeSubTab === 'orgChart' && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Organization Structure</h3>
          {orgChart.length === 0 ? (
            <p className="text-sm text-gray-400 text-center">No departments to display.</p>
          ) : (
            <div className="space-y-1">
              {orgChart.map(node => renderOrgNode(node))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DepartmentManagement;
